class Devtools {

	constructor(eventbus, websocket, async_task) {
		this.eventbus = this.createEventbus(eventbus);
		this.websocket = this.createWebsocket(websocket);
		this.taskdb = this.createTaskDB(async_task);
		this.inhouse_console = this.createInhouseConsole();
	}

	init() {
		console.log('Devtools initialized in App.devtools');
		const snapshot_key = '__devtools__snapshot';

		let snapshot = App.helper.localStorage.get(snapshot_key);

		this.eventbus.init(snapshot?.eventbus);

		window.addEventListener('beforeunload', () => {
			App.helper.localStorage.set(snapshot_key, {
				eventbus: this.eventbus.save()
			}, 30 * 24 * 3600);
		})
	}

	show_inhouse_console() {
		App.menu.hide_all();
		this.inhouse_console.toggle();
	}

	toggle_network_logging() {
		return this.inhouse_console.toggle_network_logging();
	}

	createInhouseConsole() {
		const MAX_ENTRIES = 500;
		let panel = null, list = null, input = null, menu = null, network_checkbox = null, built = false, patched = false;

		const LEVEL_STYLE = {
			log:   { color: '#e8e8e8', badge: 'LOG'  },
			info:  { color: '#5bb4ff', badge: 'INFO' },
			warn:  { color: '#ffcc66', badge: 'WARN' },
			error: { color: '#ff6b6b', badge: 'ERR'  },
			eval:  { color: '#9ad46b', badge: '>'    },
			net:   { color: '#c792ea', badge: 'NET'  },
		};

		const format_arg = (arg) => {
			if (typeof arg === 'string') return arg;
			if (arg instanceof Error) return arg.stack || (arg.name + ': ' + arg.message);
			if (arg === undefined) return 'undefined';

			try {
				return JSON.stringify(arg, (key, value) => value instanceof Node ? String(value) : value, 2);
			} catch (e) {
				return String(arg);
			}
		};

		const inject_style = () => {
			if (document.getElementById('inhouse-console-style')) return;

			const style = document.createElement('style');
			style.id = 'inhouse-console-style';
			style.textContent = `
				.inhouse-console { position: fixed; left: 0; right: 0; bottom: 0; height: 50vh; max-height: 50vh; background: #1b1d1f; color: #e8e8e8; font: 12px/1.45 SFMono-Regular, Consolas, Menlo, monospace; z-index: 2147483000; display: flex; flex-direction: column; box-shadow: 0 -2px 12px rgba(0,0,0,.4); transform: translateY(100%); transition: transform .18s ease-out; }
				.inhouse-console.is_open { transform: translateY(0); }
				.inhouse-console__header { display: flex; align-items: center; gap: 8px; padding: 8px 10px; background: #26292c; border-bottom: 1px solid #363a3d; flex: 0 0 auto; }
				.inhouse-console__title { font-weight: 600; margin-right: auto; }
				.inhouse-console__btn { background: #363a3d; color: #e8e8e8; border: 0; border-radius: 4px; padding: 4px 10px; font: inherit; cursor: pointer; }
				.inhouse-console__btn:hover { background: #454a4e; }
				.inhouse-console__log { flex: 1 1 auto; overflow-y: auto; -webkit-overflow-scrolling: touch; padding: 4px 0; }
				.inhouse-console__entry { display: flex; gap: 8px; padding: 3px 10px; border-bottom: 1px solid #232527; white-space: pre-wrap; word-break: break-word; }
				.inhouse-console__badge { flex: 0 0 auto; font-weight: 700; opacity: .8; min-width: 32px; }
				.inhouse-console__time { flex: 0 0 auto; opacity: .45; }
				.inhouse-console__msg { flex: 1 1 auto; }
				.inhouse-console__footer { display: flex; gap: 6px; padding: 8px 10px; background: #26292c; border-top: 1px solid #363a3d; flex: 0 0 auto; }
				.inhouse-console__input { flex: 1 1 auto; background: #1b1d1f !important; color: #efefef !important; border: 1px solid #363a3d; border-radius: 4px; padding: 6px 8px; font: inherit; }
				.inhouse-console__input:focus { outline: none; border-color: #5bb4ff; }
				.inhouse-console__menu-wrap { position: relative; }
				.inhouse-console__menu { position: absolute; top: 100%; left: 0; margin-top: 4px; background: #26292c; border: 1px solid #363a3d; border-radius: 6px; padding: 4px; min-width: 180px; box-shadow: 0 4px 12px rgba(0,0,0,.4); display: none; z-index: 1; }
				.inhouse-console__menu.is_open { display: block; }
				.inhouse-console__menu-item { display: flex; align-items: center; gap: 8px; padding: 6px 8px; border-radius: 4px; cursor: pointer; white-space: nowrap; }
				.inhouse-console__menu-item:hover { background: #363a3d; }
				.inhouse-console__menu-item input { margin: 0; }
			`;
			document.head.appendChild(style);
		};

		const append_entry = (level, args) => {
			if (!list) return;

			const level_style = LEVEL_STYLE[level] || LEVEL_STYLE.log;
			const at_bottom = list.scrollHeight - list.scrollTop - list.clientHeight < 24;

			const entry = document.createElement('div');
			entry.className = 'inhouse-console__entry';
			entry.style.color = level_style.color;
			entry.innerHTML =
				'<span class="inhouse-console__badge">' + level_style.badge + '</span>' +
				'<span class="inhouse-console__time">' + new Date().toLocaleTimeString() + '</span>' +
				'<span class="inhouse-console__msg"></span>';
			entry.querySelector('.inhouse-console__msg').textContent = args.map(format_arg).join(' ');

			list.appendChild(entry);

			while (list.children.length > MAX_ENTRIES) list.removeChild(list.firstChild);

			if (at_bottom) list.scrollTop = list.scrollHeight;
		};

		const patch_console = () => {
			if (patched) return;
			patched = true;

			['log', 'info', 'warn', 'error'].forEach((level) => {
				const original = console[level].bind(console);

				console[level] = (...args) => {
					original(...args);
					append_entry(level, args);
				};
			});

			window.addEventListener('error', (e) => {
				append_entry('error', [e.error ? (e.error.stack || e.error.message) : e.message]);
			});

			window.addEventListener('unhandledrejection', (e) => {
				const reason = e.reason;
				append_entry('error', ['Unhandled rejection: ' + (reason && reason.stack ? reason.stack : reason)]);
			});
		};

		let network_logging = false, network_patched = false;

		const patch_network = () => {
			if (network_patched) return;
			network_patched = true;

			const original_fetch = window.fetch ? window.fetch.bind(window) : null;

			if (original_fetch) {
				window.fetch = (resource, config, ...rest) => {
					if (!network_logging) return original_fetch(resource, config, ...rest);

					const url = typeof resource === 'string' ? resource : (resource && resource.url) || '';
					const method = (config && config.method) || (resource && resource.method) || 'GET';
					const started = Date.now();

					append_entry('net', ['→ fetch', method, url, (config && config.body) || '']);

					return original_fetch(resource, config, ...rest).then((res) => {
						append_entry('net', ['← fetch', method, url, res.status, res.statusText, (Date.now() - started) + 'ms']);
						return res;
					}).catch((err) => {
						append_entry('net', ['✕ fetch', method, url, err && err.message, (Date.now() - started) + 'ms']);
						throw err;
					});
				};
			}

			const original_open = XMLHttpRequest.prototype.open;
			const original_send = XMLHttpRequest.prototype.send;

			XMLHttpRequest.prototype.open = function (method, url, ...rest) {
				this.__devtools_method = method;
				this.__devtools_url = url;
				return original_open.call(this, method, url, ...rest);
			};

			XMLHttpRequest.prototype.send = function (body, ...rest) {
				if (!network_logging) return original_send.call(this, body, ...rest);

				const method = this.__devtools_method || 'GET';
				const url = this.__devtools_url || '';
				const started = Date.now();

				append_entry('net', ['→ xhr', method, url, body || '']);

				this.addEventListener('loadend', () => {
					append_entry('net', ['← xhr', method, url, this.status, this.statusText, (Date.now() - started) + 'ms']);
				});

				return original_send.call(this, body, ...rest);
			};
		};

		const set_network_logging = (value) => {
			patch_network();

			network_logging = value;

			append_entry('net', ['Network logging ' + (network_logging ? 'enabled' : 'disabled')]);

			if (network_checkbox) network_checkbox.checked = network_logging;
		};

		const run_eval = (code) => {
			append_entry('eval', ['> ' + code]);

			try {
				const result = (0, eval)(code);

				if (result instanceof Promise) {
					result.then((value) => append_entry('log', [value])).catch((err) => append_entry('error', [err]));
				} else if (result !== undefined) {
					append_entry('log', [result]);
				}
			} catch (e) {
				append_entry('error', [e]);
			}
		};

		const build = () => {
			if (built) return;
			built = true;

			inject_style();

			panel = document.createElement('div');
			panel.className = 'inhouse-console';
			panel.id = 'inhouse_console_container';
			panel.innerHTML =
				'<div class="inhouse-console__header">' +
					'<span class="inhouse-console__title">Inhouse Console</span>' +
					'<div class="inhouse-console__menu-wrap">' +
						'<button type="button" class="inhouse-console__btn" data-action="menu">Options ▾</button>' +
						'<div class="inhouse-console__menu" data-menu>' +
							'<label class="inhouse-console__menu-item">' +
								'<input type="checkbox" data-toggle="network_logging">' +
								'<span>Network logging</span>' +
							'</label>' +
						'</div>' +
					'</div>' +
					'<button type="button" class="inhouse-console__btn" data-action="clear">Clear</button>' +
					'<button type="button" class="inhouse-console__btn" data-action="close">Close</button>' +
				'</div>' +
				'<div class="inhouse-console__log"></div>' +
				'<div class="inhouse-console__footer">' +
					'<input type="text" class="inhouse-console__input" placeholder="Run JS…">' +
				'</div>';

			document.body.appendChild(panel);

			list = panel.querySelector('.inhouse-console__log');
			input = panel.querySelector('.inhouse-console__input');
			menu = panel.querySelector('[data-menu]');
			network_checkbox = panel.querySelector('[data-toggle="network_logging"]');

			const menu_btn = panel.querySelector('[data-action="menu"]');

			menu_btn.addEventListener('click', (e) => {
				e.stopPropagation();
				menu.classList.toggle('is_open');
			});

			document.addEventListener('click', (e) => {
				if (menu.classList.contains('is_open') && !menu.contains(e.target) && e.target !== menu_btn) {
					menu.classList.remove('is_open');
				}
			});

			network_checkbox.addEventListener('change', () => {
				set_network_logging(network_checkbox.checked);
			});

			panel.querySelector('[data-action="clear"]').addEventListener('click', () => {
				list.innerHTML = '';
			});

			panel.querySelector('[data-action="close"]').addEventListener('click', () => {
				panel.classList.remove('is_open');
			});

			input.addEventListener('keydown', (e) => {
				if (e.key !== 'Enter' || !input.value.trim()) return;

				run_eval(input.value.trim());
				input.value = '';
			});

			patch_console();
		};

		return {
			toggle() {
				build();

				panel.classList.toggle('is_open');

				if (panel.classList.contains('is_open')) input.focus();
			},
			toggle_network_logging() {
				build();
				set_network_logging(!network_logging);

				return network_logging;
			},
		};
	}

	allowNativeContextmenu(value) {
		if (typeof value === 'boolean') App.helper.localStorage.set('allow_native_contextmenu', value, 7*3600*24);

		return App.helper.localStorage.get('allow_native_contextmenu');
	}

	createWebsocket(websocket) {
		return {
			go_offline() {
				websocket.port.onmessage({ data: JSON.stringify({command: 'websocket_offline'}) });
			},
			go_online() {
				websocket.port.onmessage({ data: JSON.stringify({command: 'websocket_online'}) });
			},
		};
	}

	createEventbus(eventbus) {
		let subscriptions = new Set(), eventMap = new Map(), unseenEvents = new Set(),
			scopeTimeExec = new Map(), eventTimeExec = new Map(), show_emit_trace = false;

		function prettyStackTrace(stack) {
			const stackLines = (stack || "").split('\n')
				.slice(1)
				.map(line => line.trim().replace(/^at\s+/g, '').replace(window.location.origin, ''));

			return '\n\n' + stackLines.map((line) => `    ${line}`).join('\n') + '\n';
		}

		const log_event = function (scope, event, payload, context, stack) {
			console.log('>>>', scope + ' / ' + event, payload || 'no payload', context || 'no context', stack ? prettyStackTrace(stack) : '');
		};

		let _push = eventbus.push;

		const _calc_avg = (arr) => {
			return arr.length > 0 ? arr.reduce((acc, cur) => acc + cur, 0) / arr.length : 0;
		}

		eventbus.push = function (scope, event, payload, context) {
			const error = new Error();

			let ts = Date.now(), res = _push.apply(eventbus, [scope, event, payload, context]), diff = Date.now() - ts;

			scopeTimeExec.set(scope, [diff, ...(scopeTimeExec.get(scope) || [])].slice(0, 99));
			eventTimeExec.set(scope+'/'+event, [diff, ...(eventTimeExec.get(scope+'/'+event) || [])].slice(0, 99));

			const mapScope = eventMap.get(scope) ?? { trigger_count: 0, avg_exec: 0, events: new Map() };
			mapScope.trigger_count++;
			mapScope.avg_exec = Number(_calc_avg(scopeTimeExec.get(scope)).toFixed(2));
			eventMap.set(scope, mapScope);

			if (mapScope.events.get(event) == null) {
				unseenEvents.add(scope+'/'+event);
			}

			const mapEvent = mapScope.events.get(event) ?? { trigger_count: 0, avg_exec: 0 };
			mapEvent.trigger_count++;
			mapEvent.avg_exec = Number(_calc_avg(eventTimeExec.get(scope+'/'+event)).toFixed(2));
			mapScope.events.set(event, mapEvent);

			if (subscriptions.has(scope+'/'+event) || subscriptions.has(scope) || subscriptions.has('all')) {
				log_event(scope, event, payload, context, show_emit_trace ? error.stack : null);
			}

			return res;
		};

		return {
			init(snapshot) {
				subscriptions = snapshot?.subscriptions || subscriptions;
				eventMap = snapshot?.eventMap || eventMap;
				unseenEvents = snapshot?.unseenEvents || unseenEvents;
				show_emit_trace = snapshot?.show_emit_trace === true;
			},
			with_trace(value) {
				value ??= true;
				show_emit_trace = value === true;
			},
			show_unseen_events() {
				console.log('Unseen Events:\n    ', Array.from(unseenEvents.values()).sort((a, b) => a.localeCompare(b)).join('\n     '));
				unseenEvents.clear();
			},
			show_watching_events() {
				console.log('Watching Events:\n    ', Array.from(subscriptions.values()).sort((a, b) => a.localeCompare(b)).join('\n     '));
			},
			show_event_list(scope) {
				for (const [mainKey, mainValue] of eventMap.entries()) {
					if (scope && String(scope).toUpperCase !== String(mainKey).toUpperCase()) continue;

					console.log(`\n=== ${String(mainKey).toUpperCase()} === triggered times: ${mainValue.trigger_count} === source: App.bus.${mainKey}`);

					if (mainValue.events.size === 0) continue;

					console.table(
						Array.from(mainValue.events.entries()).map(([eventKey, eventValue]) => ({
							event: eventKey,
							'triggered times': eventValue.trigger_count,
							'avg exec, ms': eventValue.avg_exec
						})).sort((a, b) => a.event.localeCompare(b.event))
					);
				}
			},
			watch(scope, name) {
				if (scope && name) {
					subscriptions.add(scope+'/'+name);
				} else if (scope) {
					subscriptions.add(scope);
				} else {
					subscriptions.add('all');
				}
			},
			unwatch(scope, name) {
				if (scope && name) {
					subscriptions.delete(scope+'/'+name);
				} else if (scope) {
					subscriptions.delete(scope);
				} else {
					subscriptions.clear();
				}
			},
			save () {
				return {subscriptions, eventMap, unseenEvents,show_emit_trace};
			},
			clear() {
				subscriptions.clear();
				eventMap.clear();
				unseenEvents.clear();
				scopeTimeExec.clear();
				eventTimeExec.clear();
			},
		};
	}

	createTaskDB(async_task) {
		return {
			check_integrity(id_project) {

				if (!App.task_db.allowed) {
					console.error('Task DB is not allowed!');
					return;
				}

				async_task.ajax({
					id: 'devtools_taskdb_check_integrity',
					url: App.helper.endpoint.primary() + '?' + App.actions.get('batch_load:show'),
					type: 'POST',
					data: {
						updates: undefined,
						ids_project: String(id_project)
					},
					dataType: 'json',
					timeout: App.env === 'local' ? 60000 : 10000,
				}).then(async (res) => {
					const local_tasks = {};
					const remote_tasks = Object.keys(res.data).map(key => {
						let t = { id: +key };
						res.keys.forEach((field, i) => t[field] = res.data[key][i]);
						return t;
					}).reduce((a, t) => ({...a, [t.id]: t}), {});

					await App.task_db.openDB();
					await App.task_db.read_projects_from_indexedDB([id_project], local_tasks);

					const local_ids = Object.keys(local_tasks).map(key => +key);
					const remote_ids = Object.keys(remote_tasks).map(key => +key);
					const new_ids = remote_ids.filter(i => !local_ids.includes(i));

					console.log('local tasks:', local_ids.length, 'remote tasks:', remote_ids.length);

					const compare_tasks = (local, remote) => {
						const diff = [];
						res.keys.forEach((field) => {
							let local_val = local[field], remote_val = remote[field];

							if (local_val !== remote_val) {
								if (field === 'class')
								{
									const local_classes = local_val.split(' ').map(c => c.trim()).filter(c => c.length > 0);
									const remote_classes = remote_val.split(' ').map(c => c.trim()).filter(c => c.length > 0);

									const class_new = remote_classes.filter(c => !local_classes.includes(c));
									const class_old = local_classes.filter(c => !remote_classes.includes(c));

									if (class_new.length > 0 || class_old.length > 0) {
										diff.push(`${field}: add(${class_new.join(', ')}) remove(${class_old.join(', ')})`);
									}
								}
								else
								{
									diff.push(`${field}: ${local_val} => ${remote_val}`);
								}
							}
						});

						if (diff.length > 0) {
							console.log(' - ', local.id, 'not synced (diff)');
							diff.forEach(d => console.log('    ', d));
						}
					}

					new_ids.forEach(new_id => {
						console.log(' - ', new_id, 'not synced (new)');
					});

					local_ids.forEach(local_id => {
						const local = local_tasks[local_id];
						const remote = remote_tasks[local_id];

						if (remote == null) {
							console.log(' - ', local_id, 'not synced (old)');
							return;
						}

						compare_tasks(local, remote);
					});

					console.log('Done!');

				}).catch(err => {
					console.error('Error while checking integrity of DB:', err);
				})
			}
		};
	}
}
